import { BackgroundMaskService } from '@/infrastructure/engine2d/overlays/BackgroundMaskService';
import { CuttingShapeOverlayService } from '@/infrastructure/engine2d/overlays/CuttingShapeOverlayService';
import { GridOverlayService } from '@/infrastructure/engine2d/overlays/GridOverlayService';
import {
    BACKGROUND_MASK_DEFAULT_OPACITY,
    BACKGROUND_MASK_SELECTION_OPACITY,
    CANVAS_FRAME_BACKGROUND_COLOR,
    CUTTING_PATH_DEFAULT_OPACITY,
    CUTTING_PATH_SELECTION_OPACITY,
} from '@/infrastructure/engine2d/engine2dConstants';
import type { Canvas } from 'fabric';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { I2DEngine } from '@/application/ports/I2DEngine';
import type { IPrintZoneEngine2D } from '@/application/ports/IPrintZoneEngine2D';

type TFabricPrintZoneEngine2DParams = {
    engine: I2DEngine;
    scene: FabricScene;
};

// Fabric adapter for IPrintZoneEngine2D. Owns the chrome overlays
// around the printable zone: background mask + working zone, cut-path
// visibility, grid. Toggles the mask + cut opacity on selection
// changes, subscribing to I2DEngine.onSelectionChanged so the domain
// event (an entity was selected) drives the chrome, not a raw fabric
// event.
class FabricPrintZoneEngine2D implements IPrintZoneEngine2D {
    private readonly _canvas: Canvas;
    private readonly _scene: FabricScene;
    private readonly _backgroundMask: BackgroundMaskService;
    private readonly _cuttingShapeOverlay: CuttingShapeOverlayService;
    private readonly _gridOverlay: GridOverlayService;
    private readonly _unsubscribeSelection: () => void;

    constructor(params: TFabricPrintZoneEngine2DParams) {
        this._scene = params.scene;
        this._canvas = params.scene.fabricCanvas;

        this._backgroundMask = new BackgroundMaskService({
            canvas: this._canvas,
            getImageRenderHeight: () => this._scene.imageRenderHeight,
            getImageRenderWidth: () => this._scene.imageRenderWidth,
        });
        this._cuttingShapeOverlay = new CuttingShapeOverlayService({ canvas: this._canvas });
        this._gridOverlay = new GridOverlayService({
            canvas: this._canvas,
            getImageRenderHeight: () => this._scene.imageRenderHeight,
            getImageRenderWidth: () => this._scene.imageRenderWidth,
        });

        this._backgroundMask.initWorkingZone();
        this._canvas.renderAll();

        // Selection → mask + cut opacity. Uses the domain event
        // (I2DEngine.onSelectionChanged) rather than a raw fabric
        // canvas listener so the chrome depends on the port, not on
        // the concrete engine's internal event handling.
        this._unsubscribeSelection = params.engine.onSelectionChanged((entityId) => {
            if (entityId) {
                this._setMaskTransparent();
            } else {
                this._setMaskOpaque();
            }
        });
    }

    dispose(): void {
        this._unsubscribeSelection();
    }

    createGrid(url: string): Promise<void> {
        return this._gridOverlay.create(url).then(() => undefined);
    }

    deleteGrid(): void {
        this._gridOverlay.remove();
    }

    keepBackgroundMaskUpFront(): void {
        this._backgroundMask.keepMaskUpFront();
    }

    keepGridUpFront(): void {
        this._gridOverlay.keepUpFront();
    }

    resetSurface(): void {
        this._canvas.clear();
        this._canvas.backgroundColor = CANVAS_FRAME_BACKGROUND_COLOR;
        this._backgroundMask.initWorkingZone();
        this._canvas.renderAll();
    }

    setBackgroundColor(color: string): void {
        this._scene.canvasBackgroundColor = color;
        this._backgroundMask.setBackgroundColor(color);
    }

    setGridVisible(visible: boolean): void {
        this._gridOverlay.setVisibility(visible);
    }

    private _setMaskOpaque(): void {
        this._cuttingShapeOverlay.setOpacity(CUTTING_PATH_DEFAULT_OPACITY);
        this._backgroundMask.setMaskOpacity(BACKGROUND_MASK_DEFAULT_OPACITY);
    }

    private _setMaskTransparent(): void {
        this._cuttingShapeOverlay.setOpacity(CUTTING_PATH_SELECTION_OPACITY);
        this._backgroundMask.setMaskOpacity(BACKGROUND_MASK_SELECTION_OPACITY);
    }
}

export { FabricPrintZoneEngine2D };
